import type { AiModelDefaults, AiModelProviderResponse, AiModelResponse } from "@repo/shared/types";
import { useQueryClient } from "@tanstack/react-query";
import {
  CheckCircle,
  ChevronDown,
  Loader2,
  Pencil,
  Sparkles,
  Trash2,
  XCircle,
  Zap,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogCancel,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { type Vendor, VendorInfo } from "@/components/vendor-info";
import { del, get, patch, post } from "@/lib/api/_client";
import { useT } from "@/lib/i18n/use-t";
import { log } from "@/lib/logger/browser-logger";
import { T } from "./page.i18n";

const BASE = "/internal/tenant/ai-models";

// Sub-processor identity per system AI provider, keyed by the provider id from
// ai-model-service. Disclosed inside each provider's expanded card, mirroring the
// vendor blocks on the 3rd-party features page. Custom (tenant-added) providers have
// no entry — their identity is the tenant's own.
const SYSTEM_VENDORS: Record<string, Vendor> = {
  sys_requesty: {
    name: "Requesty Ltd.",
    homepage: "https://www.requesty.ai",
    details: ["United Kingdom"],
    links: [
      { kind: "terms", href: "https://www.requesty.ai/terms" },
      { kind: "status", href: "https://status.requesty.ai/" },
      { kind: "security", href: "https://www.requesty.ai/security" },
    ],
  },
  sys_mistral: {
    name: "Mistral AI (SAS)",
    homepage: "https://mistral.ai",
    details: ["RCS Paris 952 418 325", "15 rue des Halles, 75001 Paris, France"],
    links: [
      { kind: "terms", href: "https://legal.mistral.ai/terms" },
      { kind: "status", href: "https://status.mistral.ai/" },
      { kind: "security", href: "https://trust.mistral.ai/" },
    ],
  },
  sys_scaleway: {
    name: "Scaleway SAS",
    homepage: "https://www.scaleway.com",
    details: [
      "RCS Paris 433 115 904 · VAT FR35433115904",
      "8 rue de la Ville-l'Évêque, 75008 Paris, France",
    ],
    links: [
      { kind: "terms", href: "https://www.scaleway.com/en/terms/" },
      { kind: "status", href: "https://status.scaleway.com" },
      { kind: "security", href: "https://www.scaleway.com/en/security-and-resilience/" },
    ],
  },
};

export default function AiModelsPage() {
  const t = useT(T);
  const queryClient = useQueryClient();
  const [providers, setProviders] = useState<AiModelProviderResponse[]>([]);
  const [defaults, setDefaults] = useState<AiModelDefaults>({
    defaultChatModelId: null,
    defaultSimpleModelId: null,
    platformDefaultChatModelId: "",
    platformDefaultSimpleModelId: "",
    fileTranscriptionEnabled: true,
    aiAgentsEnabled: true,
    allowTrainingOnChats: true,
  });
  const [loading, setLoading] = useState(true);
  const [showAddProvider, setShowAddProvider] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [p, d] = await Promise.all([
        get<AiModelProviderResponse[]>(`${BASE}/providers`),
        get<AiModelDefaults>(`${BASE}/defaults`),
      ]);
      setProviders(p);
      setDefaults(d);
      // Invalidate chat model picker cache
      queryClient.invalidateQueries({ queryKey: ["ai-models", "available"] });
    } catch (e) {
      log.error({ err: e }, "Failed to load AI models");
    } finally {
      setLoading(false);
    }
  }, [queryClient]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const allModels = providers.flatMap((p) =>
    p.models.filter((m) => m.enabled).map((m) => ({ ...m, providerName: p.name })),
  );
  const chatModels = allModels.filter(
    (m) => m.capabilities.includes("chat") || m.capabilities.includes("tools"),
  );
  const simpleModels = allModels.filter(
    (m) => m.capabilities.includes("simple") || m.capabilities.includes("chat"),
  );
  // System-default hints: the explicit platform default ids (virtual "(auto)"
  // aggregates), falling back to list order when a platform default is hidden
  // for this tenant (e.g. a member model disabled).
  const chatDefault =
    allModels.find((m) => m.id === defaults.platformDefaultChatModelId) ?? chatModels[0];
  const simpleDefault =
    allModels.find((m) => m.id === defaults.platformDefaultSimpleModelId) ??
    allModels.find((m) => m.capabilities.includes("simple")) ??
    simpleModels[0];

  // PATCH only the changed field — the backend merges field-by-field. Sending the
  // whole `defaults` object would re-write fileTranscriptionEnabled (now owned by the
  // 3rd-party page) with a possibly-stale value loaded here.
  const handleDefaultChange = async (
    field: "defaultChatModelId" | "defaultSimpleModelId",
    value: string,
  ) => {
    const next = value && value !== "__system_default__" ? value : null;
    setDefaults((d) => ({ ...d, [field]: next }));
    await patch(`${BASE}/defaults`, { [field]: next });
  };

  const handleAiAgentsToggle = async (enabled: boolean) => {
    setDefaults((d) => ({ ...d, aiAgentsEnabled: enabled }));
    await patch(`${BASE}/defaults`, { aiAgentsEnabled: enabled });
    // Bootstrap drives the sidebar tab list — refetch so the toggle takes effect immediately.
    queryClient.invalidateQueries({ queryKey: ["app-bootstrap"] });
  };

  const handleTrainingToggle = async (enabled: boolean) => {
    setDefaults((d) => ({ ...d, allowTrainingOnChats: enabled }));
    await patch(`${BASE}/defaults`, { allowTrainingOnChats: enabled });
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const systemProviders = providers.filter((p) => p.type === "system");
  const customProviders = providers.filter((p) => p.type === "custom");

  return (
    <div className="space-y-6">
      <PageHeader
        breadcrumbs={[{ label: t.settings, href: "/settings" }, { label: t.aiSettings }]}
        title={t.aiSettings}
        description={t.pageDescription}
      />

      {/* System Models */}
      <section className="space-y-3">
        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
          {t.systemModels}
        </h2>
        {systemProviders.map((provider) => (
          <ProviderCard key={provider.id} provider={provider} onChanged={loadData} />
        ))}
      </section>

      {/* Custom Providers */}
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
            {t.customProviders}
          </h2>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowAddProvider(true)}
            disabled={customProviders.length >= 5}
          >
            {t.addProvider}
          </Button>
        </div>

        {customProviders.length === 0 && (
          <p className="text-xs text-muted-foreground text-center py-6 border border-dashed rounded-lg">
            {t.noCustomProviders}
          </p>
        )}

        {customProviders.map((provider) => (
          <ProviderCard key={provider.id} provider={provider} onChanged={loadData} />
        ))}
      </section>

      {/* Default Models */}
      <section className="space-y-4">
        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
          {t.defaultModels}
        </h2>
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <div className="text-xs text-muted-foreground mb-1">{t.chatModel}</div>
            <Select
              value={defaults.defaultChatModelId || ""}
              onValueChange={(v) => handleDefaultChange("defaultChatModelId", v)}
            >
              <SelectTrigger className="h-8 text-sm">
                <SelectValue placeholder={t.systemDefault} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__system_default__">{t.systemDefault}</SelectItem>
                {chatModels.map((m) => (
                  <SelectItem key={m.id} value={m.id}>
                    {m.providerName ? `${m.providerName} / ` : ""}
                    {m.name}
                    {m.geolocation === "global" ? "*" : ""} ({(m.contextSize / 1000).toFixed(0)}K)
                    {m.inputPrice ? ` €${m.inputPrice}/€${m.outputPrice}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!defaults.defaultChatModelId && chatDefault && (
              <p className="text-[10px] text-muted-foreground mt-1">
                System default: {chatDefault.providerName ? `${chatDefault.providerName} / ` : ""}
                {chatDefault.name}
                {chatDefault.geolocation === "global" ? "*" : ""}
              </p>
            )}
          </div>
          <div>
            <div className="text-xs text-muted-foreground mb-1">{t.simpleModel}</div>
            <Select
              value={defaults.defaultSimpleModelId || ""}
              onValueChange={(v) => handleDefaultChange("defaultSimpleModelId", v)}
            >
              <SelectTrigger className="h-8 text-sm">
                <SelectValue placeholder={t.systemDefault} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__system_default__">{t.systemDefault}</SelectItem>
                {simpleModels.map((m) => (
                  <SelectItem key={m.id} value={m.id}>
                    {m.providerName ? `${m.providerName} / ` : ""}
                    {m.name}
                    {m.geolocation === "global" ? "*" : ""} ({(m.contextSize / 1000).toFixed(0)}K)
                    {m.inputPrice ? ` €${m.inputPrice}/€${m.outputPrice}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!defaults.defaultSimpleModelId && simpleDefault && (
              <p className="text-[10px] text-muted-foreground mt-1">
                System default:{" "}
                {simpleDefault.providerName ? `${simpleDefault.providerName} / ` : ""}
                {simpleDefault.name}
                {simpleDefault.geolocation === "global" ? "*" : ""}
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Sidebar Agents */}
      <section className="space-y-3">
        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
          {t.sidebarAgents}
        </h2>
        <div className="border rounded-lg p-4 flex items-start justify-between gap-4">
          <div className="space-y-1">
            <Label htmlFor="ai-agents" className="text-sm font-medium">
              {t.aiAgentsLabel}
            </Label>
            <p className="text-xs text-muted-foreground">{t.aiAgentsHint}</p>
          </div>
          <Switch
            id="ai-agents"
            checked={defaults.aiAgentsEnabled}
            onCheckedChange={handleAiAgentsToggle}
          />
        </div>
      </section>

      {/* Training */}
      <section className="space-y-3">
        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
          {t.training}
        </h2>
        <div className="border rounded-lg p-4 flex items-start justify-between gap-4">
          <div className="space-y-1">
            <Label htmlFor="allow-training" className="text-sm font-medium">
              {t.allowTraining}
            </Label>
            <p className="text-xs text-muted-foreground">{t.allowTrainingHint}</p>
          </div>
          <Switch
            id="allow-training"
            checked={defaults.allowTrainingOnChats}
            onCheckedChange={handleTrainingToggle}
          />
        </div>
      </section>

      <p className="text-[11px] text-muted-foreground pt-4 border-t">
        {t.pricingNote}
        <br />
        <span className="font-medium">*</span> {t.globalFootnotePre}{" "}
        <span className="font-mono">global</span> {t.globalFootnotePost}
      </p>

      {/* Add Provider Dialog */}
      <ProviderDialog open={showAddProvider} onOpenChange={setShowAddProvider} onSaved={loadData} />
    </div>
  );
}

// ─── Provider Card ──────────────────────────────────────────

function ProviderCard({
  provider,
  onChanged,
}: {
  provider: AiModelProviderResponse;
  onChanged: () => void;
}) {
  const t = useT(T);
  const [expanded, setExpanded] = useState(false);
  const [addModelOpen, setAddModelOpen] = useState(false);
  const [editModel, setEditModel] = useState<AiModelResponse | null>(null);
  const isSystem = provider.type === "system";
  const vendor = SYSTEM_VENDORS[provider.id];

  const handleToggle = async (enabled: boolean) => {
    try {
      await patch(`${BASE}/providers/${provider.id}`, { enabled });
    } catch {
      // Refetch below restores the switch to the server state.
    }
    onChanged();
  };

  const handleDelete = async () => {
    await del(`${BASE}/providers/${provider.id}`);
    onChanged();
  };

  const handleModelToggle = async (modelId: string, enabled: boolean) => {
    await patch(`${BASE}/models/${modelId}`, { enabled });
    onChanged();
  };

  const handleDeleteModel = async (modelId: string) => {
    await del(`${BASE}/models/${modelId}`);
    onChanged();
  };

  return (
    <div className="border rounded-lg p-4 space-y-3">
      <div className="flex items-center justify-between">
        <button
          type="button"
          className="flex items-center gap-2 flex-1 text-left"
          onClick={() => setExpanded((v) => !v)}
        >
          <Sparkles className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium text-sm">{provider.name}</span>
          {isSystem && (
            <span className="text-[9px] px-1.5 py-0.5 rounded bg-primary/10 text-primary">
              {t.systemBadge}
            </span>
          )}
          <span className="text-xs text-muted-foreground">
            {provider.models.length} {provider.models.length === 1 ? t.model : t.models}
          </span>
          <ChevronDown
            className={`h-3 w-3 text-muted-foreground transition-transform ${expanded ? "rotate-180" : ""}`}
          />
        </button>
        <div className="flex items-center gap-2">
          <Switch
            aria-label={t.enabled}
            checked={provider.enabled}
            onCheckedChange={handleToggle}
            disabled={isSystem}
          />
          {!isSystem && (
            <Button variant="ghost" size="icon" tooltip={t.deleteProvider} onClick={handleDelete}>
              <Trash2 className="h-3.5 w-3.5 text-muted-foreground" />
            </Button>
          )}
        </div>
      </div>

      {expanded && (
        <div className="space-y-1 pl-6">
          {!isSystem && (
            <div className="text-[10px] text-muted-foreground truncate mb-2">{provider.url}</div>
          )}

          {provider.models.map((model) => (
            <ModelRow
              key={model.id}
              model={model}
              isSystem={isSystem}
              onToggle={(enabled) => handleModelToggle(model.id, enabled)}
              onDelete={() => handleDeleteModel(model.id)}
              onEdit={() => setEditModel(model)}
            />
          ))}

          {!isSystem && (
            <Button
              variant="ghost"
              size="sm"
              className="text-xs text-muted-foreground mt-1"
              onClick={() => setAddModelOpen(true)}
            >
              {t.addModel}
            </Button>
          )}

          {vendor && (
            <div className="pt-3 mt-2 border-t">
              <VendorInfo vendor={vendor} />
            </div>
          )}
        </div>
      )}

      {/* Add Model Dialog */}
      <ModelDialog
        open={addModelOpen}
        onOpenChange={setAddModelOpen}
        providerId={provider.id}
        onSaved={onChanged}
      />

      {/* Edit Model Dialog */}
      <ModelDialog
        open={editModel !== null}
        onOpenChange={(open) => !open && setEditModel(null)}
        providerId={provider.id}
        model={editModel ?? undefined}
        onSaved={onChanged}
      />
    </div>
  );
}

// ─── Model Row ──────────────────────────────────────────────

function ModelRow({
  model,
  isSystem,
  onToggle,
  onDelete,
  onEdit,
}: {
  model: AiModelResponse;
  isSystem: boolean;
  onToggle: (enabled: boolean) => void;
  onDelete: () => void;
  onEdit: () => void;
}) {
  const t = useT(T);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{
    success: boolean;
    error?: string;
    responseTime?: number;
  } | null>(null);

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const result = await post<{
        success: boolean;
        error?: string;
        responseTime?: number;
      }>(`${BASE}/models/${model.id}/test`, {});
      setTestResult(result);
    } catch (e: any) {
      setTestResult({ success: false, error: `${e}` });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="flex items-center justify-between text-xs py-1.5 border-b border-muted/30 last:border-0">
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <span className="font-medium truncate">
          {model.name}
          {model.geolocation === "global" && (
            <span className="text-muted-foreground ml-0.5" title={t.globalRoutingNote}>
              *
            </span>
          )}
        </span>
        <span className="text-muted-foreground text-[10px]">
          {(model.contextSize / 1000).toFixed(0)}K
        </span>
        {model.inputPrice && (
          <span className="text-muted-foreground text-[10px]">
            €{model.inputPrice}/€{model.outputPrice}
          </span>
        )}
        {testResult && (
          <span className="flex items-center gap-1">
            {testResult.success ? (
              <CheckCircle className="h-3 w-3 text-success" />
            ) : (
              <XCircle className="h-3 w-3 text-destructive" />
            )}
            {testResult.responseTime && (
              <span className="text-[10px] text-muted-foreground">
                {(testResult.responseTime / 1000).toFixed(1)}s
              </span>
            )}
          </span>
        )}
      </div>
      <div className="flex items-center gap-1">
        {!isSystem && (
          <>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleTest}
              tooltip={t.testModel}
              loading={testing}
            >
              <Zap className="h-3 w-3" />
            </Button>
            <Button variant="ghost" size="icon" onClick={onEdit} tooltip={t.editModel}>
              <Pencil className="h-3 w-3" />
            </Button>
          </>
        )}
        <Switch aria-label={t.enabled} checked={model.enabled} onCheckedChange={onToggle} />
        {!isSystem && (
          <Button variant="ghost" size="icon" tooltip={t.deleteModel} onClick={onDelete}>
            <Trash2 className="h-3 w-3 text-muted-foreground" />
          </Button>
        )}
      </div>
    </div>
  );
}

// ─── Provider Dialog (Add) ──────────────────────────────────

function ProviderDialog({
  open,
  onOpenChange,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
}) {
  const t = useT(T);
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!name || !url || !apiKey) return;
    setSaving(true);
    try {
      await post(`${BASE}/providers`, { name, url, apiKey, models: [] });
      setName("");
      setUrl("");
      setApiKey("");
      onOpenChange(false);
      onSaved();
    } catch (e) {
      log.error({ err: e }, "Failed to save AI provider");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t.addProvider}</DialogTitle>
          <DialogDescription>{t.addProviderDescription}</DialogDescription>
        </DialogHeader>

        <div className="space-y-5 pt-4">
          <div className="space-y-2">
            <Label>
              {t.name} <span className="text-destructive">*</span>
            </Label>
            <Input
              placeholder={t.namePlaceholder}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label>
              {t.apiUrl} <span className="text-destructive">*</span>
            </Label>
            <Input
              placeholder={t.urlPlaceholder}
              value={url}
              onChange={(e) => setUrl(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label>
              {t.apiKey} <span className="text-destructive">*</span>
            </Label>
            <Input
              type="password"
              placeholder={t.keyPlaceholder}
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-4">
          <DialogCancel>{t.cancel}</DialogCancel>
          <Button onClick={handleSubmit} disabled={!name || !url || !apiKey} loading={saving}>
            {t.addProvider}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Model Dialog (Add / Edit) ──────────────────────────────

function ModelDialog({
  open,
  onOpenChange,
  providerId,
  model,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  providerId: string;
  model?: AiModelResponse;
  onSaved: () => void;
}) {
  const t = useT(T);
  const isEdit = !!model;
  const [name, setName] = useState(model?.name || "");
  const [code, setCode] = useState(model?.code || "");
  const [contextSize, setContextSize] = useState(model ? String(model.contextSize / 1000) : "128");
  const [inputPrice, setInputPrice] = useState(model?.inputPrice || "");
  const [outputPrice, setOutputPrice] = useState(model?.outputPrice || "");
  const [saving, setSaving] = useState(false);

  // Reset form when model changes
  useEffect(() => {
    setName(model?.name || "");
    setCode(model?.code || "");
    setContextSize(model ? String(model.contextSize / 1000) : "128");
    setInputPrice(model?.inputPrice || "");
    setOutputPrice(model?.outputPrice || "");
  }, [model]);

  const handleSubmit = async () => {
    if (!name || !code) return;
    setSaving(true);
    try {
      const data = {
        name,
        code,
        contextSize: (Number.parseInt(contextSize, 10) || 128) * 1000,
        inputPrice: inputPrice || null,
        outputPrice: outputPrice || null,
      };

      if (isEdit && model) {
        await patch(`${BASE}/models/${model.id}`, data);
      } else {
        await post(`${BASE}/models`, { providerId, ...data });
      }

      onOpenChange(false);
      onSaved();
    } catch (e) {
      log.error({ err: e }, "Failed to save AI model");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEdit ? t.editModelTitle : t.addModelTitle}</DialogTitle>
          <DialogDescription>
            {isEdit ? t.editModelDescription : t.addModelDescription}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 pt-4">
          <div className="space-y-2">
            <Label>
              {t.displayName} <span className="text-destructive">*</span>
            </Label>
            <Input
              placeholder={t.displayNamePlaceholder}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label>
              {t.modelCode} <span className="text-destructive">*</span>
            </Label>
            <Input
              placeholder={t.modelCodePlaceholder}
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label>{t.contextSize}</Label>
            <Input
              type="number"
              placeholder="128"
              value={contextSize}
              onChange={(e) => setContextSize(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label>{t.inputPrice}</Label>
            <Input
              type="number"
              step="0.01"
              placeholder={t.costTrackingHint}
              value={inputPrice}
              onChange={(e) => setInputPrice(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label>{t.outputPrice}</Label>
            <Input
              type="number"
              step="0.01"
              placeholder={t.costTrackingHint}
              value={outputPrice}
              onChange={(e) => setOutputPrice(e.target.value)}
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-4">
          <DialogCancel>{t.cancel}</DialogCancel>
          <Button onClick={handleSubmit} disabled={!name || !code} loading={saving}>
            {isEdit ? t.save : t.addModel}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
